import * as React from 'react';
import { useId } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
    ArrayDataType,
    BooleanDataType,
    DataType,
    NumberDataType,
    NumberLiteralDataType,
    ObjectDataType,
    ShapeDataType,
    StringDataType,
    StringLiteralDataType,
    typeAMatchesTypeB,
    ShapeValue,
    UserDefinedType,
    createOptional,
    isOptionalType,
    unwrapOptional,
} from '@glueapp/graph-types';
import { useCallback, useMemo } from 'react';
import { ShapeStringLiteralEditor } from './ShapeStringLiteralEditor';
import { ShapeNumberLiteralEditor } from './ShapeNumberLiteralEditor';
import { ShapeArrayEditor } from './ShapeArrayEditor';
import { ShapeObjectEditor } from './ShapeObjectEditor';

const renderEditField = (value: DataType, onChange: (value: DataType) => void) => {
    switch (value.kind) {
        case 'string':
        case 'number':
        case 'boolean':
        case 'shape':
        case 'union':
        case 'generic':
            return null;
        case 'optional':
            return renderEditField(unwrapOptional(value), onChange);
        case 'stringLiteral':
            return <ShapeStringLiteralEditor value={value as StringLiteralDataType} onChange={onChange} />;
        case 'numberLiteral':
            return <ShapeNumberLiteralEditor value={value as NumberLiteralDataType} onChange={onChange} />;
        case 'array':
            return <ShapeArrayEditor value={value as ArrayDataType} onChange={onChange} />;
        case 'object':
            return <ShapeObjectEditor value={value as ObjectDataType} onChange={onChange} />;
    }
};

const StringOption: StringDataType = { kind: 'string' };
const NumberOption: NumberDataType = { kind: 'number' };
const BooleanOption: BooleanDataType = { kind: 'boolean' };
const StringLiteralOption: StringLiteralDataType = { kind: 'stringLiteral', stringLiteral: '' };
const NumberLiteralOption: NumberLiteralDataType = { kind: 'numberLiteral', numberLiteral: 0 };
const ObjectOption: ObjectDataType = { kind: 'object', restriction: 'open', properties: {} };
const ArrayOption: ArrayDataType = { kind: 'array', subType: { kind: 'string' } };
const SHAPE_TYPE_SELECTOR_OPTIONS: { [key: string]: DataType } = {
    [StringOption.kind]: StringOption,
    [NumberOption.kind]: NumberOption,
    [BooleanOption.kind]: BooleanOption,
    [StringLiteralOption.kind]: StringLiteralOption,
    [NumberLiteralOption.kind]: NumberLiteralOption,
    [ObjectOption.kind]: ObjectOption,
    [ArrayOption.kind]: ArrayOption,
};

const ShapeTypeSelectorOptionKindToLabel: Map<string, string> = new Map([
    [StringOption.kind, 'Text'],
    [NumberOption.kind, 'Number'],
    [BooleanOption.kind, 'Boolean'],
    [StringLiteralOption.kind, 'A specific Text'],
    [NumberLiteralOption.kind, 'A specific Number'],
    [ObjectOption.kind, 'Object'],
    [ArrayOption.kind, 'List'],
]);

interface ShapeTypeSelectorEditorProps {
    value: DataType;
    restriction?: DataType;
    onChange: (value: DataType) => void;
}

export const ShapeTypeSelectorEditor: React.FC<ShapeTypeSelectorEditorProps> = ({ value, restriction, onChange }) => {
    const optionalToggleId = useId();
    const optional = isOptionalType(value);
    const innerType = unwrapOptional(value);

    const filteredOptions: DataType[] = useMemo(() => {
        if (restriction) {
            return Object.values(SHAPE_TYPE_SELECTOR_OPTIONS).filter(option => typeAMatchesTypeB(option, restriction));
        } else {
            return Object.values(SHAPE_TYPE_SELECTOR_OPTIONS);
        }
    }, [restriction]);

    const handleSelectChange = useCallback(
        (selectedValue: string) => {
            const selectedOption = SHAPE_TYPE_SELECTOR_OPTIONS[selectedValue];
            if (selectedOption) {
                onChange(optional ? createOptional(selectedOption) : selectedOption);
            }
        },
        [onChange, optional],
    );

    const handleOptionalToggle = useCallback(
        (checked: boolean | 'indeterminate') => {
            onChange(checked === true ? createOptional(innerType) : innerType);
        },
        [onChange, innerType],
    );

    const handleInnerTypeChange = useCallback(
        (newInnerType: DataType) => {
            onChange(optional ? createOptional(newInnerType) : newInnerType);
        },
        [onChange, optional],
    );

    return (
        <>
            <Select value={innerType.kind} onValueChange={handleSelectChange} disabled={filteredOptions.length < 2}>
                <SelectTrigger>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {filteredOptions.map((option, index) => (
                        <SelectItem key={index} value={option.kind}>
                            {ShapeTypeSelectorOptionKindToLabel.get(option.kind)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {!restriction && (
                <div className="flex items-center gap-2 mt-1">
                    <Checkbox id={optionalToggleId} checked={optional} onCheckedChange={handleOptionalToggle} />
                    <label htmlFor={optionalToggleId} className="text-xs text-muted-foreground cursor-pointer select-none">
                        Optional
                    </label>
                </div>
            )}
            {renderEditField(innerType, handleInnerTypeChange)}
        </>
    );
};

interface ShapeValueEditorProps {
    type: ShapeDataType;
    value?: ShapeValue;
    onChange: (value: ShapeValue, updatedType?: UserDefinedType) => void;
}

export const ShapeValueEditor: React.FC<ShapeValueEditorProps> = ({ type, value, onChange }) => {
    return (
        <div className="flex flex-col pt-1">
            <ShapeTypeSelectorEditor
                value={value?.type ?? { kind: 'string' }}
                restriction={type.restriction}
                onChange={newValue => onChange({ isShapeValue: true, type: newValue })}
            />
        </div>
    );
};
